<template>
  <AccountTenantsContext
    v-slot="{ isTenant, isOnTenantRoute }"
    :is-auth="isAuthenticated && !!idTokenClaims"
    :is-fullscreen="isFullScreen"
  >
    <div
      v-if="!hasError"
      class="duration-500"
      :class="v2LayoutEnabled ? '' : 'max-w-7xl mx-auto'"
    >
      <div
        class="flex"
        :class="{
          'h-[calc(100vh-3rem)] sm:h-[calc(100vh-3.5rem)]': v2LayoutEnabled && isTenant,
          'h-screen': v2LayoutEnabled && !isTenant,
          'min-h-screen': !v2LayoutEnabled,
        }"
      >
        <SideNavigation v-if="!isFullScreen" :class="isTenant ? 'sm:pt-12' : ''" />

        <!-- Main content -->
        <ResizableContext
          :is-auth="isAuthenticated && !!idTokenClaims"
          :is-tenant="isTenant"
          :is-on-tenant-route="isOnTenantRoute"
          :is-fullscreen="isFullScreen"
          class=""
          :class="isFullScreen ? '' : 'sm:pl-16 md:pl-24'"
        >
          <main
            :class="
              route.meta.disableWrapperClasses ? '' : 'px-2 sm:px-4 md:px-6 pb-6 sm:pb-0'
            "
            class="grow overflow-hidden duration-300 min-h-full flex flex-col @container/main"
          >
            <div
              :class="
                route.meta.disableWrapperClasses
                  ? ''
                  : v2LayoutEnabled
                    ? ''
                    : 'max-w-[1072px] mx-auto'
              "
              class="grow w-full h-full flex flex-col duration-300"
            >
              <Header
                v-if="
                  (!isFullScreen && !route.meta.hideHeader) ||
                  route.name === RouteNames.ONBOARDING
                "
                :is-on-tenant-route="isOnTenantRoute"
                :is-fullscreen="isFullScreen"
                @sign-out="doLogout"
              />

              <!-- MABanner removed, using addBanner in watcher instead -->

              <AppBannerWrapper
                v-if="!isFullScreen && !route.meta.hideBanners"
                :class="{
                  'empty:hidden': v2LayoutEnabled && isTenant,
                }"
              />
              <!-- <Hero /> -->

              <!-- Content -->
              <div
                class="grow @cmd/main:flex not-supports-cq:md:flex space-y-8 md:space-y-0 gap-x-8"
                :class="{
                  'pb-16 md:pb-20': !v2LayoutEnabled && !route.meta.disableWrapperClasses,
                  'pt-2': v2LayoutEnabled,
                  'sm:pb-2 min-h-full': v2LayoutEnabled,
                  'min-h-[80vh]': !v2LayoutEnabled,
                }"
              >
                <slot></slot>

                <!-- Render-less -->
                <SocketContext v-if="footerReady && !isLocal" />
              </div>
              <!-- allows page to scroll to bottom on visit -->
              <span id="v2-layout-bottom" class="block h-1 w-full"></span>

              <Footer
                :class="
                  route.meta.hideFooter
                    ? 'fixed top-full opacity-0 pointer-events-none'
                    : ''
                "
                @ready="footerReady = true"
              />
            </div>
          </main>
        </ResizableContext>
        <QuickTaskWrapper />
        <V2ChatWrapper
          v-if="
            !isFullScreen &&
            !currentSessionInformation.initializingSession &&
            !isOnTenantRoute
          "
        />
      </div>

      <TestLayer> </TestLayer>
    </div>
    <!-- no-auth routes -->
    <slot v-else></slot>

    <HelpSidebar :is-tenant="isTenant" />
    <Teleport to="body">
      <AppHelpButton v-if="!isLocal" />
    </Teleport>
  </AccountTenantsContext>
</template>
<script setup lang="ts">
import Bugsnag from "@bugsnag/js";
import SideNavigation from "../partials/SideNavigation.vue";
import Header from "../partials/Header.vue";
import Footer from "../partials/Footer.vue";
import SocketContext from "./SocketContext.vue";
import TestLayer from "./TestLayer.vue";

import AppBannerWrapper from "@/components/shared/AppBannerWrapper.vue";
import AccountTenantsContext from "./AccountTenantsContext.vue";
import ResizableContext from "./ResizableContext.vue";
import HelpSidebar from "@/components/help/HelpSidebar.vue";
import AppHelpButton from "@/components/shared/AppHelpButton.vue";
import { useAuth0 } from "@auth0/auth0-vue";
import QuickTaskWrapper from "@/components/quickTask/QuickTaskWrapper.vue";
const V2ChatWrapper = defineAsyncComponent(
  () => import("@/components/v2/widget/V2WidgetWrapper.vue")
);

const router = useRouter();
const route = useRoute();
const { t } = withI18nHelpers();
const billingStore = useBillingStore();
const { v2LayoutEnabled } = useV2Layout();
// socketContext tries to teleport to footer sometimes before it's in the DOM
const footerReady = ref(false);
const hasError = ref(false);
const { loadingAuthentication, isAuthenticated, idTokenClaims, user } = useCurrentUser();
const auth0 = useAuth0();
const { data: subscription } = useSubscription(
  computed(() => !!currentSessionInformation.appAuthToken)
);
const { maybeShowBanners } = useBillingBanners();
const tourStore = useTourStore();
const { addBanner, bannerStore } = useAppBanner();
// const ready = ref(false);
const isLocal = import.meta.env.MODE === "local-test";

const isFullScreen = computed(
  () =>
    !!router.currentRoute.value.meta.isFullScreen ||
    router.currentRoute.value.path.startsWith("/tenant") ||
    router.currentRoute.value.name === RouteNames.ONBOARDING ||
    router.currentRoute.value.name === RouteNames.WELCOME ||
    router.currentRoute.value.name === RouteNames.PAYMENT_FAILED ||
    router.currentRoute.value.name === RouteNames.THIRD_PARTY ||
    router.currentRoute.value.name === RouteNames.RESTRICTED_VIEW ||
    router.currentRoute.value.name === RouteNames.MAINTENANCE_VIEW ||
    (!hasFeatureFlag("freeTrialNoStripe") &&
      router.currentRoute.value.name === RouteNames.PLANS)
);
async function doLogout() {
  await router.push({ name: RouteNames.LOGOUT });
}
const authLoadingState = computed(() => {
  const state = {
    shown: false,
    message: undefined as string | undefined,
  };
  if (loadingAuthentication.value || currentSessionInformation.initializingSession) {
    state.shown = true;
    state.message = t("auth.signInWaitingMessage");
  } else if (route.name === RouteNames.INITIALIZE) {
    return {
      shown: true,
      message: "",
    };
  }
  return state;
});

watch(user, currentVal => {
  if (!currentVal?.integrations?.length) return;
  const linkedInIntegrations = currentVal.integrations.filter(
    (integration: { id: string; [key: string]: unknown }) =>
      integration.id.startsWith("linkedin")
  );
  const hasOldClientId = linkedInIntegrations.some(integration =>
    Boolean((integration as { oldClientId?: boolean }).oldClientId)
  );
  const bannerId = 45687676532;
  const bannerExists = bannerStore.some(banner => banner.id === bannerId);
  if (hasOldClientId && !bannerExists) {
    addBanner({
      id: bannerId,
      title: `${t("notice")}`,
      message: `${t("linkedInReconnect")}`,
      dismissible: true,
      link: "/settings/integrations",
      linkText: "Go to Integrations",
      type: "warning",
    });
  }
});

watch(subscription, v => {
  if (v) {
    billingStore.setSubscription(v);
    maybeShowBanners();
  }
});

watch(appAuthToken, async v => {
  if (v && import.meta.env.MODE === "local-test") {
    const response = await useGetRequest<Record<string, any>>("/get-test-claims", {
      baseURL: "http://localhost:3001",
      headers: {
        Authorization: `Bearer ${appAuthToken.value}`,
      },
    });
    const claims = response.data;
    if (claims) setCurrentSessionClaims(claims);
  }
});

watch(user, currentVal => {
  if (!currentVal) return;
  tourStore.setTourAccountData(currentVal?.welcomeTour);
  currentSessionInformation.preferredLanguage = currentVal.locale || "en";
  if (Bugsnag.isStarted()) {
    Bugsnag.setUser(currentVal.id, currentVal.name, currentVal.email);
    Bugsnag.addMetadata("user", {
      tenantId: currentVal.tenantId,
      activeAccountId: currentVal.activeAccountId,
    });
  }
  // first time
  identifyAnalyticsSession(currentVal);
});
defineExpose({ authLoadingState });

onBeforeMount(() => {
  auth0
    .handleRedirectCallback()
    .then(data => {
      const appState = data.appState;
      if (!appState) return;
      const referralData = appState.referralData;
      if (!referralData) return;
      localStorage.setItem("referralData", referralData);
    })
    .catch(e => {
      const message: string = e?.message || "";
      // do nothing
      if (message.includes("query") || message.includes("params")) auth0.error.value = "";
    });
});

onMounted(async () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const oauth_token = params.get("oauth_token");
  const oauth_verifier = params.get("oauth_verifier");
  const integrationState = sessionStorage.getItem("integration-state");
  const integrationType = sessionStorage.getItem("integration");
  const isIntegration = state?.includes("integration=true") || false;
  const hasIntegrationParam = params.has("integration");

  if (isIntegration || hasIntegrationParam) {
    const oauthParams = new URLSearchParams();
    if (code) oauthParams.set("code", code);
    if (state) oauthParams.set("state", state);
    if (oauth_token) oauthParams.set("oauth_token", oauth_token);
    if (oauth_verifier) oauthParams.set("oauth_verifier", oauth_verifier);
    oauthParams.set("integration", "true"); // Ensure integration flag is preserved
    // Add a small delay to prevent race conditions
    await new Promise(resolve => setTimeout(resolve, 100));

    await router.push({
      name: RouteNames.THIRD_PARTY,
      query: Object.fromEntries(oauthParams),
    });
    return;
  }

  if (
    (integrationType === "twitter" && oauth_token && oauth_verifier) ||
    (integrationType !== "twitter" && state === integrationState)
  ) {
    if (window.opener && typeof window.opener.postMessage === "function") {
      window.opener.postMessage({
        code,
        state,
        oauth_token,
        oauth_verifier,
      });
      window.close();
    }
  }
});

// temp
const redesignedRoutes = [
  // RouteNames.CONTENT_CALENDAR,
  // RouteNames.CONTENT_LIST,
  RouteNames.ONBOARDING,
];

watchEffect(() => {
  if (!document || !document.body) return;
  if (redesignedRoutes.includes(route.name as symbol)) {
    document.body.classList.add(`bg-ma-page!`);
  } else {
    document.body.classList.remove(`bg-ma-page!`);
  }
});
</script>
